import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import styles from "../passwordModal/assignModal.module.css";
import { Close } from "../../../Svg";
import { updateProductInventory } from "../../../redux/productSlice";
import { useDispatch } from "react-redux";

const statusOptions = ["OUT OF STOCK", "PUBLISHED", "DRAFT"];

const UpdateInventoryModal = ({ isOpen, onClose, onAssign, datas }) => {

    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const [focusedField, setFocusedField] = useState(null);

    const formik = useFormik({
        initialValues: {
            status: datas?.status || "PUBLISHED",
            qtyAvailable: datas?.inventory?.qtyAvailable ?? 0,
            rows: (Array.isArray(datas?.quantityTable) && datas.quantityTable.length > 0)
                ? datas.quantityTable.map((r) => ({
                    breakQty: r?.breakQty ?? "",
                    unitPrice: (r?.unitPrice === null || r?.unitPrice === undefined) ? "" : r.unitPrice,
                }))
                : [{ breakQty: 1000, unitPrice: "" }],
        },
        validationSchema: Yup.object({
            status: Yup.string().required("Status is required"),
            qtyAvailable: Yup.number()
                .typeError("Must be a number")
                .required("Stock is required"),
            rows: Yup.array().of(
                Yup.object({
                    breakQty: Yup.number()
                        .typeError("Must be a number")
                        .positive("Must be greater than 0")
                        .required("Required"),
                    unitPrice: Yup.number()
                        .transform((value, originalValue) => originalValue === "" ? null : value)
                        .typeError("Must be a number")
                        .min(0, "Cannot be negative")
                        .nullable(),
                })
            ).min(1, "At least one break quantity is required"),
        }),
        enableReinitialize: true,
        onSubmit: async (values) => {
            setLoading(true);
            const payloadValues = {
                status: values.status,
                qtyAvailable: (values.qtyAvailable !== "" && values.qtyAvailable !== null && values.qtyAvailable !== undefined) ? Number(values.qtyAvailable) : 0,
                quantityTable: values.rows.map((r) => ({
                    breakQty: Number(r.breakQty),
                    unitPrice: (r.unitPrice !== "" && r.unitPrice !== null && r.unitPrice !== undefined) ? Number(r.unitPrice) : 0,
                })),
            };
            const data = {
                id: datas._id,
                values: payloadValues
            };
            const status = await dispatch(updateProductInventory(data));
            if (status.payload == 200 || status.type?.endsWith('/fulfilled')) {
                onClose();
            }
            setLoading(false);
        },
    });

    if (!isOpen) return null;

    const prodNum = datas?.productOverview?.productNumber?.[0] || datas?.productOverview?.productNumber;

    const mfgRaw = datas?.productOverview?.manufacturer;
    const manufacturerName = Array.isArray(mfgRaw)
        ? mfgRaw.map((m) => (typeof m === 'string' ? '' : m?.name)).filter(Boolean).join(', ')
        : (mfgRaw?.name || '');

    const spqRaw = datas?.inventory?.minimumMultiplier
        || (Array.isArray(datas?.additionalResources?.Standard_Packaging_Quantity)
            ? datas.additionalResources.Standard_Packaging_Quantity[0]
            : datas?.additionalResources?.Standard_Packaging_Quantity);
    const spq = Number(spqRaw) > 0 ? Number(spqRaw) : null;

    const rows = Array.isArray(formik.values.rows) ? formik.values.rows : [];

    const addRow = () => {
        formik.setFieldValue('rows', [...rows, { breakQty: "", unitPrice: "" }]);
    };

    const removeRow = (index) => {
        if (rows.length <= 1) return;
        formik.setFieldValue('rows', rows.filter((_, i) => i !== index));
        if (Array.isArray(formik.touched.rows)) {
            formik.setTouched({ ...formik.touched, rows: formik.touched.rows.filter((_, i) => i !== index) }, false);
        }
    };

    const getInputStyle = (fieldName) => ({
        width: '100%',
        height: '42px',
        padding: '0 14px',
        borderRadius: '8px',
        border: focusedField === fieldName ? '1.5px solid #512DA8' : '1px solid #D0D5DD',
        boxShadow: focusedField === fieldName ? '0 0 0 3px rgba(81, 45, 168, 0.12)' : 'none',
        fontSize: '14px',
        color: '#101828',
        background: focusedField === fieldName ? '#FFFFFF' : '#FAFAFC',
        fontFamily: `'Public Sans', sans-serif`,
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'all 0.15s ease-in-out'
    });

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(15, 23, 42, 0.5)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300
        }}>
            <div style={{
                background: '#FFFFFF',
                borderRadius: '16px',
                width: '100%',
                maxWidth: '520px',
                padding: '28px 32px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.18)',
                boxSizing: 'border-box'
            }}>
                {/* Modal Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '22px' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h2 style={{ margin: 0, fontSize: '19px', fontWeight: '700', color: '#101828', fontFamily: `'Public Sans', sans-serif` }}>Update Inventory</h2>
                            {prodNum && (
                                <span style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    padding: '2px 8px',
                                    borderRadius: '6px',
                                    background: '#F0EEFF',
                                    color: '#512DA8',
                                    fontFamily: `'Public Sans', sans-serif`
                                }}>
                                    {prodNum}
                                </span>
                            )}
                        </div>
                        {(manufacturerName || spq) && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginTop: '8px', fontSize: '13px', color: '#344054', fontFamily: `'Public Sans', sans-serif` }}>
                                {manufacturerName && (
                                    <span><span style={{ color: '#667085' }}>Manufacturer: </span><strong style={{ fontWeight: '600' }}>{manufacturerName}</strong></span>
                                )}
                                {spq && (
                                    <span><span style={{ color: '#667085' }}>SPQ: </span><strong style={{ fontWeight: '600' }}>{spq.toLocaleString('en-IN')}</strong></span>
                                )}
                            </div>
                        )}
                        <p style={{ margin: '5px 0 0 0', fontSize: '13px', color: '#667085', fontFamily: `'Public Sans', sans-serif` }}>
                            Update status, stock quantity, and pricing details
                        </p>
                    </div>
                    <button 
                        type="button" 
                        onClick={onClose} 
                        style={{
                            background: '#F2F4F7',
                            border: 'none',
                            cursor: 'pointer',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#667085',
                            transition: 'all 0.15s ease'
                        }}
                    >
                        <Close />
                    </button>
                </div>

                <form onSubmit={formik.handleSubmit}>
                    {/* Status Dropdown */}
                    <div style={{ marginBottom: '18px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#344054', marginBottom: '6px', fontFamily: `'Public Sans', sans-serif` }}>
                            Status
                        </label>
                        <select
                            name="status"
                            onChange={formik.handleChange}
                            onBlur={(e) => { formik.handleBlur(e); setFocusedField(null); }}
                            onFocus={() => setFocusedField('status')}
                            value={formik.values.status}
                            style={getInputStyle('status')}
                        >
                            <option value="">Select Status...</option>
                            {statusOptions.map((row) => (
                                <option key={row} value={row}>
                                    {row}
                                </option>
                            ))}
                        </select>
                        {formik.touched.status && formik.errors.status && (
                            <div style={{ color: '#D92D20', fontSize: '12px', marginTop: '4px' }}>{formik.errors.status}</div>
                        )}
                    </div>

                    {/* Stock Input */}
                    <div style={{ marginBottom: '18px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#344054', marginBottom: '6px', fontFamily: `'Public Sans', sans-serif` }}>
                            Stock (Available Quantity)
                        </label>
                        <input
                            type="text"
                            name="qtyAvailable"
                            placeholder="e.g. 5000"
                            onChange={formik.handleChange}
                            onBlur={(e) => { formik.handleBlur(e); setFocusedField(null); }}
                            onFocus={() => setFocusedField('qtyAvailable')}
                            value={formik.values.qtyAvailable}
                            style={getInputStyle('qtyAvailable')}
                        />
                        {formik.touched.qtyAvailable && formik.errors.qtyAvailable && (
                            <div style={{ color: '#D92D20', fontSize: '12px', marginTop: '4px' }}>{formik.errors.qtyAvailable}</div>
                        )}
                    </div>

                    {/* Break quantity / unit price rows */}
                    <div style={{ marginBottom: '26px' }}>
                        <div style={{ display: 'flex', gap: '14px', width: '100%', marginBottom: '6px' }}>
                            <label style={{ flex: 1, fontSize: '13px', fontWeight: '600', color: '#344054', fontFamily: `'Public Sans', sans-serif` }}>
                                Break Qty
                            </label>
                            <label style={{ flex: 1, fontSize: '13px', fontWeight: '600', color: '#344054', fontFamily: `'Public Sans', sans-serif` }}>
                                Unit Price <span style={{ color: '#667085', fontWeight: '400' }}>(optional)</span>
                            </label>
                            <div style={{ width: '32px', flexShrink: 0 }} />
                        </div>

                        {rows.map((row, index) => {
                            const rowTouched = formik.touched.rows?.[index] || {};
                            const rowErrors = (typeof formik.errors.rows?.[index] === 'object' && formik.errors.rows[index]) || {};
                            return (
                                <div key={index} style={{ display: 'flex', gap: '14px', width: '100%', alignItems: 'flex-start', marginBottom: '10px' }}>
                                    <div style={{ flex: 1 }}>
                                        <input
                                            type="text"
                                            name={`rows.${index}.breakQty`}
                                            placeholder="e.g. 1000"
                                            onChange={formik.handleChange}
                                            onBlur={(e) => { formik.handleBlur(e); setFocusedField(null); }}
                                            onFocus={() => setFocusedField(`rows.${index}.breakQty`)}
                                            value={row.breakQty}
                                            style={getInputStyle(`rows.${index}.breakQty`)}
                                        />
                                        {rowTouched.breakQty && rowErrors.breakQty && (
                                            <div style={{ color: '#D92D20', fontSize: '12px', marginTop: '4px' }}>{rowErrors.breakQty}</div>
                                        )}
                                    </div>

                                    <div style={{ flex: 1 }}>
                                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                            <span style={{
                                                position: 'absolute',
                                                left: '12px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#667085',
                                                fontFamily: `'Public Sans', sans-serif`
                                            }}>
                                                ₹
                                            </span>
                                            <input
                                                type="text"
                                                name={`rows.${index}.unitPrice`}
                                                placeholder="0.00"
                                                onChange={formik.handleChange}
                                                onBlur={(e) => { formik.handleBlur(e); setFocusedField(null); }}
                                                onFocus={() => setFocusedField(`rows.${index}.unitPrice`)}
                                                value={row.unitPrice}
                                                style={{
                                                    ...getInputStyle(`rows.${index}.unitPrice`),
                                                    paddingLeft: '28px'
                                                }}
                                            />
                                        </div>
                                        {rowTouched.unitPrice && rowErrors.unitPrice && (
                                            <div style={{ color: '#D92D20', fontSize: '12px', marginTop: '4px' }}>{rowErrors.unitPrice}</div>
                                        )}
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => removeRow(index)}
                                        disabled={rows.length <= 1}
                                        title="Remove"
                                        style={{
                                            width: '32px',
                                            height: '42px',
                                            flexShrink: 0,
                                            border: 'none',
                                            background: 'transparent',
                                            color: rows.length <= 1 ? '#D0D5DD' : '#D92D20',
                                            fontSize: '20px',
                                            lineHeight: 1,
                                            cursor: rows.length <= 1 ? 'not-allowed' : 'pointer',
                                            fontFamily: `'Public Sans', sans-serif`
                                        }}
                                    >
                                        &times;
                                    </button>
                                </div>
                            );
                        })}

                        {typeof formik.errors.rows === 'string' && (
                            <div style={{ color: '#D92D20', fontSize: '12px', marginTop: '4px' }}>{formik.errors.rows}</div>
                        )}

                        <button
                            type="button"
                            onClick={addRow}
                            style={{
                                marginTop: '4px',
                                height: '36px',
                                padding: '0 14px',
                                borderRadius: '8px',
                                border: '1px dashed #512DA8',
                                background: '#F0EEFF',
                                color: '#512DA8',
                                fontSize: '13px',
                                fontWeight: '600',
                                fontFamily: `'Public Sans', sans-serif`,
                                cursor: 'pointer'
                            }}
                        >
                            + Add Break Qty
                        </button>
                    </div>

                    {/* Footer Buttons */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '20px', borderTop: '1px solid #F2F4F7' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                height: '42px',
                                padding: '0 20px',
                                borderRadius: '8px',
                                border: '1px solid #D0D5DD',
                                background: '#FFFFFF',
                                color: '#344054',
                                fontSize: '14px',
                                fontWeight: '600',
                                fontFamily: `'Public Sans', sans-serif`,
                                cursor: 'pointer',
                                transition: 'all 0.15s ease'
                            }}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            disabled={loading}
                            style={{
                                height: '42px',
                                padding: '0 26px',
                                borderRadius: '8px',
                                border: 'none',
                                background: '#512DA8',
                                color: '#FFFFFF',
                                fontSize: '14px',
                                fontWeight: '600',
                                fontFamily: `'Public Sans', sans-serif`,
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(81, 45, 168, 0.3)',
                                opacity: loading ? 0.7 : 1,
                                transition: 'all 0.15s ease'
                            }}
                        >
                            {loading ? "Updating..." : "Update"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UpdateInventoryModal;
